/* The Action Center — turns project state into a short, prioritised list of
   things that need someone's attention, for the studio (per project) and the
   client (their portal). Pure derivation over existing data. */

import type { Project } from "./types";

export interface Todo { key: string; label: string; detail?: string; section: string; urgent?: boolean }

const todayStr = () => new Date().toISOString().slice(0, 10);
const overdue = (dueOn?: string) => !!dueOn && dueOn < todayStr();

function openInvoices(p: Project) { return (p.invoices ?? []).filter((i) => i.status === "due" || i.status === "overdue"); }

/** What the client still needs to do — respects the studio's feature toggles. */
export function clientTodos(p: Project): Todo[] {
  const hidden = new Set(p.portalHidden ?? []);
  const t: Todo[] = [];
  const show = (section: string) => !hidden.has(section);

  if (show("brief") && p.brief?.status === "sent") t.push({ key: "brief", label: "Complete your design brief", detail: "The studio is waiting on your input.", section: "brief", urgent: true });

  /* The concept choice gates everything after it — lead with it. */
  const opts = p.deck?.status === "issued" ? p.deck.options : [];
  if (show("concept") && opts.length > 0 && !opts.some((o) => o.response?.choice === "preferred"))
    t.push({ key: "concept", label: `Choose a design direction from ${opts.length} option${opts.length === 1 ? "" : "s"}`, detail: "The next stage builds on whichever you pick.", section: "concept", urgent: true });

  /* A design proposal has no options to choose, so the ask is acceptance. */
  if (show("concept") && p.deck?.status === "issued" && !p.deck.acceptance && (p.deck.options ?? []).length === 0)
    t.push({ key: "deckAccept", label: "Accept the design proposal, or ask for changes", detail: "Nothing moves to the next stage until you have.", section: "concept", urgent: true });

  const pendingDesigns = (p.presentation?.spaces ?? []).filter((s: any) => s.response === "pending").length;
  if (show("designs") && pendingDesigns) t.push({ key: "designs", label: `Approve ${pendingDesigns} design${pendingDesigns === 1 ? "" : "s"}`, section: "designs", urgent: true });

  if (show("engagement") && p.engagement?.status === "issued") t.push({ key: "engagement", label: "Review & sign the engagement", section: "engagement", urgent: true });

  if (show("billing")) {
    const due = openInvoices(p);
    const od = due.filter((i) => i.status === "overdue" || overdue(i.dueOn)).length;
    if (due.length) t.push({ key: "billing", label: `${due.length} invoice${due.length === 1 ? "" : "s"} to pay`, detail: od ? `${od} overdue` : undefined, section: "billing", urgent: od > 0 });
    const vIssued = (p.variations ?? []).filter((v) => v.status === "issued").length;
    if (vIssued) t.push({ key: "variations", label: `Approve ${vIssued} variation${vIssued === 1 ? "" : "s"}`, section: "billing", urgent: true });
  }

  if (show("reviews")) {
    let toApprove = 0;
    for (const r of (p.reviews ?? [])) { if (!r.sharedWithClient) continue; for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client" && m.response && !m.response.accepted) toApprove++; }
    if (toApprove) t.push({ key: "reviews", label: `Approve ${toApprove} revised implementation${toApprove === 1 ? "" : "s"}`, section: "reviews" });
  }

  if (show("rfis")) {
    const mine = (p.rfis ?? []).filter((r) => r.status !== "closed" && r.ballInCourt === "client").length;
    if (mine) t.push({ key: "rfis", label: `Respond to ${mine} RFI${mine === 1 ? "" : "s"}`, section: "rfis", urgent: true });
  }
  return t;
}

/** What the studio owes on this project. */
export function studioTodos(p: Project): Todo[] {
  const t: Todo[] = [];

  let openRemarks = 0;
  for (const r of (p.reviews ?? [])) for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client" && !m.response) openRemarks++;
  if (openRemarks) t.push({ key: "review", label: `Respond to ${openRemarks} change request${openRemarks === 1 ? "" : "s"}`, section: "review", urgent: true });

  const rfiOurs = (p.rfis ?? []).filter((r) => r.status !== "closed" && r.ballInCourt === "studio").length;
  if (rfiOurs) t.push({ key: "rfis", label: `Answer ${rfiOurs} RFI${rfiOurs === 1 ? "" : "s"}`, section: "rfis", urgent: true });

  const od = openInvoices(p).filter((i) => i.status === "overdue" || overdue(i.dueOn)).length;
  if (od) t.push({ key: "billing", label: `${od} invoice${od === 1 ? "" : "s"} overdue`, section: "billing", urgent: true });

  const claims = (p.invoices ?? []).some((i) => (i.payments ?? []).some((pm) => pm.status === "claimed"));
  if (claims) t.push({ key: "ledger", label: "Confirm a client payment claim", section: "billing", urgent: true });

  const vDraft = (p.variations ?? []).filter((v) => v.status === "draft").length;
  if (vDraft) t.push({ key: "variations", label: `Issue ${vDraft} draft variation${vDraft === 1 ? "" : "s"}`, section: "billing" });

  const brief = p.brief?.status === "changes" ? 1 : 0;
  if (brief) t.push({ key: "brief", label: "Client requested brief changes", section: "brief" });

  /* The concept: a draft nobody has seen, and comments nobody has answered. */
  if (p.deck && p.deck.status === "draft" && p.deck.slides.length > 1)
    t.push({ key: "deck", label: "Concept presentation still in draft", detail: `${p.deck.slides.length} slides, ${p.deck.options.length} options — not yet sent`, section: "concept" });
  if (p.deck?.acceptance?.status === "changes")
    t.push({ key: "deckChanges", label: "The client asked for changes to the presentation", section: "concept", urgent: true });

  const deckQs = (p.deck?.notes ?? []).filter((n) => n.author === "client" && !n.reply).length;
  if (deckQs) t.push({ key: "deckNotes", label: `Answer ${deckQs} comment${deckQs === 1 ? "" : "s"} on the concept`, section: "concept", urgent: true });

  const fb = (p.feedback ?? []).filter((f) => !f.resolved && f.author === "client").length;
  if (fb) t.push({ key: "feedback", label: `${fb} open client message${fb === 1 ? "" : "s"}`, section: "feedback" });

  return t;
}
